import {
  Bar,
  BarChart,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

const formatCompactNumber = (value) => {
  if (value < 1000) {
    return value.toString();
  } else if (value < 100000) {
    return `${Math.floor(value / 1000)}K`;
  } else if (value < 1000000) {
    return `${Math.floor(value / 1000)}K`;
  }
  return `${Math.floor(value / 1000000)}M`;
};

export function GenericBarChart({
  title = "Bar Chart",
  data = [],
  dataKey = "value",
  xAxisKey = "label",
  barColor = "#3b82f6",
  totalAmount = "$0",
  percentageChange = 0,
  dateRange = "",
  tooltipFormatter = (value) => `$${value.toLocaleString()}`,
  tooltipTitle = "Value",
  tooltipSubtitle = "Count",
  onViewDetail = null,
  onBarClick,
  showBorder = true,
  showHeader = true,
  showMetrics = true,
}) {
  return (
    <div
      className={`h-full flex flex-col overflow-hidden bg-white rounded-lg ${
        showBorder ? "border border-solid border-gray-300  shadow-sm" : ""
      }`}
    >
      {/* Header - Always visible */}
      {showHeader && (
        <div className="p-2 sm:p-3 md:p-4 shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="summary-card__title">{title}</h3>
            <div className="flex items-center gap-2 sm:gap-4 ml-2">
              <div className="hidden sm:flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: barColor }}
                ></div>
                <span className="text-xs text-gray-600 truncate">
                  {tooltipTitle}
                </span>
              </div>
              {onViewDetail && (
                <button
                  className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
                  onClick={onViewDetail}
                >
                  View Detail
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Main Content */}
      <div className="p-2 pt-0 sm:p-3 md:p-4 sm:pt-0 md:pt-0 flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Key Metrics - Collapsible on small screens */}
        {showMetrics && (
          <div className="mb-2 sm:mb-4 min-h-fit">
            <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-900 truncate">
              {totalAmount}
            </div>
            {percentageChange !== 0 && (
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span
                  className={
                    percentageChange >= 0 ? "text-green-500" : "text-red-500"
                  }
                >
                  {percentageChange >= 0 ? "↑" : "↓"}{" "}
                  {Math.abs(percentageChange).toFixed(2)}%
                </span>
                <span className="text-gray-400 hidden sm:inline">change</span>
              </div>
            )}
            {dateRange && (
              <div className="text-xs sm:text-sm text-gray-500 truncate">
                {dateRange}
              </div>
            )}
          </div>
        )}

        {/* Chart - Priority display */}
        <div className="flex-1 min-h-[120px] overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
              onClick={(e) =>
                onBarClick &&
                onBarClick({
                  label: e.activeLabel,
                })
              }
            >
              <XAxis
                dataKey={xAxisKey}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                tickMargin={8}
                interval="preserveStartEnd"
                minTickGap={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickFormatter={formatCompactNumber}
                width={30}
              />
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                horizontal={true}
                vertical={false}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-2 border border-gray-300 rounded-lg shadow-lg">
                        <p className="text-xs font-medium text-gray-900 mb-1">{`${label}`}</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: barColor }}
                          ></div>
                          <p className="text-xs text-gray-700">
                            {tooltipFormatter(payload[0].value)}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
                cursor={{ fill: `${barColor}1A` }}
              />
              <Bar
                dataKey={dataKey}
                fill={barColor}
                radius={[2, 2, 0, 0]}
                maxBarSize={50}
                minPointSize={2}
                onClick={(data) => onBarClick && onBarClick(data)} // Add click handler
                cursor="pointer" // Add pointer cursor
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
